import React, { useState, useRef, useEffect, createContext, useContext, useMemo } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { Check, ChevronDown } from 'lucide-react';
import { cn } from '../../lib/utils';

const SelectContext = createContext({});

function Select({ children, value, onValueChange, defaultValue, displayValue }) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState(value || defaultValue || '');
  const [internalDisplayValue, setInternalDisplayValue] = useState(displayValue || '');
  const selectRef = useRef(null);
  const contentRef = useRef(null);

  useEffect(() => {
    if (value !== undefined) {
      setSelectedValue(value);
    }
  }, [value]);

  useEffect(() => {
    if (displayValue) {
      setInternalDisplayValue(displayValue);
    }
  }, [displayValue]);

  useEffect(() => {
    function handleClickOutside(event) {
      // Check if click is outside both the select trigger and the content portal
      const isOutsideTrigger = selectRef.current && !selectRef.current.contains(event.target);
      const isOutsideContent = contentRef.current && !contentRef.current.contains(event.target);

      if (isOutsideTrigger && isOutsideContent) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (newValue, label) => {
    setSelectedValue(newValue);
    setInternalDisplayValue(label);
    setIsOpen(false);
    if (onValueChange) {
      onValueChange(newValue);
    }
  };

  const contextValue = useMemo(
    () => ({
      isOpen,
      setIsOpen,
      selectedValue,
      internalDisplayValue,
      handleSelect,
      selectRef,
      contentRef,
    }),
    [isOpen, selectedValue, internalDisplayValue],
  );

  return (
    <SelectContext.Provider value={contextValue}>
      <div ref={selectRef} className="relative">
        {children}
      </div>
    </SelectContext.Provider>
  );
}

Select.propTypes = {
  children: PropTypes.node.isRequired,
  value: PropTypes.string,
  onValueChange: PropTypes.func,
  defaultValue: PropTypes.string,
  displayValue: PropTypes.string,
};

Select.defaultProps = {
  value: undefined,
  onValueChange: undefined,
  defaultValue: '',
  displayValue: '',
};

function SelectTrigger({ children, className }) {
  const { isOpen, setIsOpen } = useContext(SelectContext);

  return (
    <button
      type="button"
      onClick={() => setIsOpen(!isOpen)}
      className={cn(
        'flex h-9 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm',
        'ring-offset-background placeholder:text-muted-foreground',
        'focus:outline-none focus:ring-1 focus:ring-ring',
        'disabled:cursor-not-allowed disabled:opacity-50',
        className,
      )}
      aria-expanded={isOpen}
    >
      {children}
      <ChevronDown
        className={cn(
          'h-4 w-4 opacity-50 transition-transform shrink-0 ml-1',
          isOpen && 'rotate-180',
        )}
      />
    </button>
  );
}

SelectTrigger.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
};

SelectTrigger.defaultProps = {
  className: '',
};

function SelectValue({ placeholder }) {
  const { selectedValue, internalDisplayValue } = useContext(SelectContext);

  if (!selectedValue) {
    return <span className="text-muted-foreground truncate">{placeholder}</span>;
  }

  return <span className="truncate">{internalDisplayValue || selectedValue}</span>;
}

SelectValue.propTypes = {
  placeholder: PropTypes.string,
};

SelectValue.defaultProps = {
  placeholder: 'Select...',
};

function SelectContent({ children, className }) {
  const { isOpen, selectRef, contentRef } = useContext(SelectContext);
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });

  useEffect(() => {
    if (isOpen && selectRef.current) {
      const rect = selectRef.current.getBoundingClientRect();
      setPosition({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    }
  }, [isOpen, selectRef]);

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div
      ref={contentRef}
      className={cn(
        'fixed z-[9999] min-w-[8rem] overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md',
        'animate-in fade-in-0 zoom-in-95',
        className,
      )}
      style={{
        top: position.top,
        left: position.left,
        width: position.width,
      }}
    >
      <div className="p-1 max-h-60 overflow-auto">{children}</div>
    </div>,
    document.body,
  );
}

SelectContent.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
};

SelectContent.defaultProps = {
  className: '',
};

function SelectItem({ children, value, className }) {
  const { selectedValue, handleSelect } = useContext(SelectContext);
  const isSelected = selectedValue === value;
  const labelRef = useRef(null);

  const handleClick = (e) => {
    e.stopPropagation();
    const label = labelRef.current?.textContent || children;
    handleSelect(value, label);
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      className={cn(
        'relative flex w-full cursor-pointer select-none items-center rounded-sm py-1.5 pl-2 pr-8 text-sm outline-none',
        'hover:bg-accent hover:text-accent-foreground',
        'focus:bg-accent focus:text-accent-foreground',
        isSelected && 'bg-accent',
        className,
      )}
    >
      <span ref={labelRef} className="truncate">
        {children}
      </span>
      {isSelected && (
        <span className="absolute right-2 flex h-3.5 w-3.5 items-center justify-center">
          <Check className="h-4 w-4" />
        </span>
      )}
    </button>
  );
}

SelectItem.propTypes = {
  children: PropTypes.node.isRequired,
  value: PropTypes.string.isRequired,
  className: PropTypes.string,
};

SelectItem.defaultProps = {
  className: '',
};

export { Select, SelectTrigger, SelectValue, SelectContent, SelectItem };
export default Select;
